import { store, useSelector } from 'redux/store';
import {
  findWorkTasks,
  useFindWorkTasks as useFindWorkTasksMutation,
  FindWorkTasksParams,
  FindWorkTasksBody,
  GetWorkTasksRequest,
  WorkTask as WorkTaskDto,
  useCompleteWorkTask as useApiClientCompleteWorkTask,
  useRestartWorkTask as useApiClientRestartWorkTask,
  useGetWorkTaskFilterOptions as useApiClientGetWorkTaskFilterOptions,
  getWorkTaskFilterOptions,
  getGetWorkTaskFilterOptionsQueryKey,
  GetWorkTaskFilterOptionsParams,
  WorkTaskFilterType,
  filterWorkTaskIds,
  FilterWorkTaskIdsParams,
  WorkTaskFilterCriteria,
  WorkTaskFilterIdsResult,
  useGetWorkTaskTypes as useApiClientGetWorkTaskTypes,
  getWorkTaskTypes,
  getGetWorkTaskTypesQueryKey,
  GetWorkTaskTypesParams,
  WorkTaskTypeCatalogItem,
  useCreateWorkTask as useApiClientCreateWorkTask,
  CreateWorkTask,
  CreateWorkTask201,
} from 'api-client';
import { useAuthContext } from 'identityAndAccess/infrastructure/useAuthContext';
import { QueryClient, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { queryClient as moduleQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import loggerService, { LoggerLevel } from '../log';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { workTaskCacheManager } from './cache-management';
import { buildWorkTaskFilterCriteria, WorkTaskFilters } from './filterCriteria';
import { useEffect, useMemo, useRef } from 'react';
import { WorkTaskPriority } from '@planitgeo/treeplotter-domain/lib/modules/work/work-task/domain/work-task.interface';

/** Shared by useFindWorkTasks and its prefetch so both land on the same query key. */
const findWorkTasksQueryKey = (params: FindWorkTasksParams, filterBody?: WorkTaskFilterCriteria) =>
  ['findWorkTasks', params, filterBody] as const;

// Type for mutation options (inferred from generated API client)
type MutationOptions<TData, TError, TVariables, TContext> = {
  mutation?: import('@tanstack/react-query').UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >;
};

const WorkTaskRepository = {
  useFindWorkTasks(
    params?: FindWorkTasksParams,
    filters?: WorkTaskFilters,
    options?: { enabled?: boolean }
  ) {
    const { auth } = store.getState();
    const { isAuthenticated } = useAuthContext();
    const tenantId = TenantRepository.getCurrentTenantPid();
    const queryClient = useQueryClient();

    const queryParams: FindWorkTasksParams = useMemo(
      () => ({
        ...params,
        tenantId,
      }),
      [params, tenantId]
    );

    const filterStatus = filters?.status?.join(',');
    const filterAssignee = filters?.assignee;
    const filterTaskType = filters?.taskType?.join(',');
    const filterPriority = filters?.priority?.join(',');
    const filterWorkOrderName = JSON.stringify(filters?.workOrderName);

    const filterBody: WorkTaskFilterCriteria | undefined = useMemo(
      () => buildWorkTaskFilterCriteria(filters),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filterStatus, filterAssignee, filterTaskType, filterPriority, filterWorkOrderName]
    );

    // Note: findWorkTasks is now a POST endpoint but we use it with useQuery for fetching
    // When no body is provided, the endpoint behaves like a GET with query params
    // When filters are provided, we pass them in the body
    const shouldBeEnabled = isAuthenticated && !!auth.user && !!tenantId;
    const useQueryResponse = useQuery({
      queryKey: findWorkTasksQueryKey(queryParams, filterBody),
      queryFn: async ({ signal }) => {
        const body: FindWorkTasksBody | undefined = filterBody;
        return findWorkTasks(body, queryParams, signal);
      },
      enabled: options?.enabled === false ? false : shouldBeEnabled,
      placeholderData: keepPreviousData,
    });

    const data: WorkTaskDto[] | undefined = useQueryResponse?.data;
    const isFetching = useQueryResponse?.isFetching;

    // Track previous fetching state to detect when a fetch completes
    const prevIsFetchingRef = useRef<boolean>(false);
    // Track if we've already evicted after the last fetch to prevent loops
    const hasEvictedAfterLastFetchRef = useRef<boolean>(false);

    // Work task cache eviction based on device storage
    useEffect(() => {
      const fetchJustCompleted = prevIsFetchingRef.current && !isFetching;
      prevIsFetchingRef.current = isFetching;

      // Reset eviction flag when a new fetch starts
      if (isFetching) {
        hasEvictedAfterLastFetchRef.current = false;
      }

      // Only process eviction if:
      // 1. We have data
      // 2. A fetch just completed (new data from server)
      // 3. We haven't already evicted after this fetch
      if (
        !data ||
        data.length === 0 ||
        !fetchJustCompleted ||
        hasEvictedAfterLastFetchRef.current
      ) {
        return;
      }

      const storageAnalysis = workTaskCacheManager.analyzeStorage(data);

      if (storageAnalysis.shouldEvict) {
        const evictionResult = workTaskCacheManager.planEviction(data, storageAnalysis);

        if (evictionResult.evictedCount > 0) {
          // Mark that we've evicted after this fetch to prevent loops
          hasEvictedAfterLastFetchRef.current = true;

          loggerService.log(LoggerLevel.INFO, 'Work task cache eviction triggered', {
            reason: storageAnalysis.reason,
            severity: storageAnalysis.severity,
            storageUtilizationPercent: storageAnalysis.storageUtilizationPercent,
            availableBytes: storageAnalysis.availableBytes,
            workTaskCount: storageAnalysis.workTaskCount,
            evictedCount: evictionResult.evictedCount,
            remainingCount: evictionResult.remainingCount,
            oldestEvictedHLC: evictionResult.oldestEvictedTimestamp,
            newestEvictedHLC: evictionResult.newestEvictedTimestamp,
          });

          // Remove evicted tasks from cache
          queryClient.setQueryData(
            ['findWorkTasks', queryParams, filterBody],
            (oldData: WorkTaskDto[] | undefined) =>
              oldData?.filter((t) => !evictionResult.evictedIds.includes(t.id)) || []
          );
        }
      }
    }, [data, isFetching, queryParams, filterBody, queryClient]);

    if (!data || data?.length === 0) {
      return {
        ...useQueryResponse,
        workTaskEntities: null,
      };
    }

    // Note: Keeping DTOs for read-side operations instead of mapping to entities.
    // WorkTaskEntity is domain-focused and lacks read-side fields (tp2OrgId, assignedUserId,
    // createdAt, updatedAt, asset object) that the UI needs. The DTO provides the full
    // read model data required for UI display and filtering.
    const workTaskEntities = data;

    return {
      ...useQueryResponse,
      workTaskEntities,
    };
  },

  /**
   * Fetch work tasks for a set of asset IDs (e.g. on-screen assets for map priority).
   * Uses POST findWorkTasks with body { filters: [{ field: 'asset_id', values: assetIds }] }.
   * Query key includes sorted asset IDs so cache is per viewport set.
   */
  useFindWorkTasksByAssetIds(
    assetIds: string[],
    params?: FindWorkTasksParams,
    options?: { enabled?: boolean }
  ) {
    const { auth } = store.getState();
    const { isAuthenticated } = useAuthContext();
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryParams: FindWorkTasksParams = useMemo(
      () => ({
        ...params,
        tenantId,
      }),
      [params, tenantId]
    );

    const assetIdsKey = useMemo(
      () => (assetIds.length > 0 ? [...assetIds].sort().join(',') : ''),
      [assetIds]
    );

    const filterBody: WorkTaskFilterCriteria | undefined = useMemo(
      () =>
        assetIds.length > 0 ? { filters: [{ field: 'asset_id', values: assetIds }] } : undefined,
      [assetIds]
    );

    const shouldBeEnabled = isAuthenticated && !!auth.user && !!tenantId && assetIds.length > 0;
    const useQueryResponse = useQuery({
      queryKey: ['findWorkTasksByAssetIds', assetIdsKey, queryParams],
      queryFn: async ({ signal }) => {
        const body: FindWorkTasksBody | undefined = filterBody;
        return findWorkTasks(body, queryParams, signal);
      },
      enabled: options?.enabled === false ? false : shouldBeEnabled,
      placeholderData: keepPreviousData,
    });

    const data: WorkTaskDto[] | undefined = useQueryResponse?.data;

    if (!data || data.length === 0) {
      return {
        ...useQueryResponse,
        workTaskEntities: null,
      };
    }

    return {
      ...useQueryResponse,
      workTaskEntities: data,
    };
  },

  /**
   * Fetch work tasks by IDs with optional HLC filtering.
   * Uses mutation since POST body is required for ID-based fetching.
   */
  useFindWorkTasksByIds<TError = unknown, TContext = unknown>(
    options?: MutationOptions<
      WorkTaskDto[],
      TError,
      { data: FindWorkTasksBody; params?: FindWorkTasksParams },
      TContext
    >,
    queryClient?: QueryClient
  ) {
    return useFindWorkTasksMutation(options, queryClient);
  },

  useCompleteWorkTask<TError = unknown, TContext = unknown>(
    options?: MutationOptions<
      void,
      TError,
      { tenantId: string; workTaskId: string; data: unknown },
      TContext
    >,
    queryClient?: QueryClient
  ) {
    return useApiClientCompleteWorkTask(options, queryClient);
  },

  useRestartWorkTask<TError = unknown, TContext = unknown>(
    options?: MutationOptions<
      void,
      TError,
      { tenantId: string; workTaskId: string; data: unknown },
      TContext
    >,
    queryClient?: QueryClient
  ) {
    return useApiClientRestartWorkTask(options, queryClient);
  },

  useCreateWorkTask<TError = unknown, TContext = unknown>(
    options?: MutationOptions<
      CreateWorkTask201 | void,
      TError,
      { tenantId: string; workTaskId: string; data: CreateWorkTask },
      TContext
    >,
    queryClient?: QueryClient
  ) {
    return useApiClientCreateWorkTask(options, queryClient);
  },

  async refetchWorkTasks(queryClient: QueryClient, params?: FindWorkTasksParams) {
    await queryClient.invalidateQueries({
      queryKey: ['findWorkTasks', params],
    });
  },

  async prefetchWorkTasks(tenantId: string, params?: FindWorkTasksParams) {
    if (!tenantId) {
      return;
    }

    try {
      const queryParams: FindWorkTasksParams = {
        ...params,
        tenantId,
      };

      await moduleQueryClient.prefetchQuery({
        queryKey: findWorkTasksQueryKey(queryParams),
        queryFn: async ({ signal }) => {
          const { auth } = store.getState();
          if (!auth.isAuthenticated || !auth.user) {
            return [];
          }
          // Pass undefined as body to use query params mode
          const response = await findWorkTasks(undefined, queryParams, signal);
          return response;
        },
      });
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, error);
    }
  },

  async prefetchWorkTaskTypes() {
    const tenantId = TenantRepository.getCurrentTenantPid();
    if (!tenantId) {
      return;
    }

    const params: GetWorkTaskTypesParams = { tenantId };
    await moduleQueryClient.prefetchQuery({
      queryKey: getGetWorkTaskTypesQueryKey(params),
      queryFn: ({ signal }) => getWorkTaskTypes(params, signal),
    });
  },

  async prefetchWorkTaskFilterOptions(filterTypes: WorkTaskFilterType[]) {
    const tenantId = TenantRepository.getCurrentTenantPid();
    if (!tenantId) {
      return;
    }

    const params: GetWorkTaskFilterOptionsParams = { tenantId, filterTypes };
    await moduleQueryClient.prefetchQuery({
      queryKey: getGetWorkTaskFilterOptionsQueryKey(params),
      queryFn: ({ signal }) => getWorkTaskFilterOptions(params, signal),
    });
  },

  useGetWorkTaskFilterOptions(filterTypes: WorkTaskFilterType[]) {
    const { auth } = store.getState();
    const { isAuthenticated } = useAuthContext();
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryParams: GetWorkTaskFilterOptionsParams = {
      tenantId,
      filterTypes,
    };

    return useApiClientGetWorkTaskFilterOptions(queryParams, {
      query: {
        enabled: isAuthenticated && !!auth.user && !!tenantId && filterTypes.length > 0,
      },
    });
  },

  useGetWorkTaskTypeCatalog(options?: { enabled?: boolean }) {
    const { isAuthenticated } = useAuthContext();
    const user = useSelector((state) => state.auth.user);
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryParams: GetWorkTaskTypesParams = { tenantId };

    const useQueryResponse = useApiClientGetWorkTaskTypes(queryParams, {
      query: {
        enabled: (options?.enabled ?? true) && isAuthenticated && !!user && !!tenantId,
      },
    });

    const taskTypes: WorkTaskTypeCatalogItem[] = useQueryResponse.data?.taskTypes ?? [];

    return { ...useQueryResponse, taskTypes };
  },

  /**
   * Filter work task IDs based on filter criteria.
   * Returns work task IDs with HLC timestamps for staleness detection.
   *
   * @param criteria - Filter criteria (task type, assigned to, status, etc.)
   * @param params - Request parameters including tenantId
   * @returns Promise resolving to object with items array containing id/hlcTimestamp pairs
   */
  async filterWorkTaskIds(
    criteria: WorkTaskFilterCriteria,
    params: FilterWorkTaskIdsParams
  ): Promise<WorkTaskFilterIdsResult> {
    return filterWorkTaskIds(criteria, params);
  },

  /**
   * Fetch work tasks by their IDs.
   * Used for fetching specific work tasks when only IDs are known.
   *
   * @param workTaskIds - Array of work task IDs to fetch
   * @param tenantId - Tenant ID for the request
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of work task DTOs
   */
  async findWorkTasksByIds(
    workTaskIds: string[],
    tenantId: string,
    signal?: AbortSignal
  ): Promise<WorkTaskDto[]> {
    if (!workTaskIds || workTaskIds.length === 0) {
      return [];
    }

    const requestBody: GetWorkTasksRequest = {
      workTasks: workTaskIds.map((id) => ({ id })),
    };

    const response = await findWorkTasks(requestBody, { tenantId }, signal);
    return response || [];
  },

  /**
   * Fetch work tasks with filters (status, assignee, task type).
   * Imperative version for use in navigation flows.
   *
   * @param filters - Filter criteria object with status, assignee, taskType
   * @param params - Optional query parameters (limit, offset, tp2OrgId)
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to array of work task DTOs
   */
  async getWorkTasks(
    filters?: {
      status?: ('open' | 'completed')[];
      assignee?: string;
      taskType?: string[];
      priority?: WorkTaskPriority[];
      workOrderName?: string[];
    },
    params?: {
      limit?: number;
      offset?: number;
      tp2OrgId?: number;
    },
    signal?: AbortSignal
  ): Promise<WorkTaskDto[]> {
    const tenantId = TenantRepository.getCurrentTenantPid();
    if (!tenantId) {
      return [];
    }

    const queryParams: FindWorkTasksParams = {
      ...params,
      tenantId,
    };

    let filterBody: WorkTaskFilterCriteria | undefined;

    if (filters) {
      const serverFilters: Array<{ field: string; values: string[] }> = [];

      if (filters.status && filters.status.length > 0) {
        // Map status values to server format (capitalize first letter)
        serverFilters.push({
          field: 'status',
          values: filters.status.map((s) => (s === 'open' ? 'Open' : 'Completed')),
        });
      }

      if (filters.assignee && filters.assignee !== 'all') {
        serverFilters.push({
          field: 'assigned_to',
          values: [filters.assignee],
        });
      }

      if (filters.taskType && filters.taskType.length > 0) {
        serverFilters.push({
          field: 'task_type',
          values: filters.taskType,
        });
      }

      if (filters.priority && filters.priority.length > 0) {
        serverFilters.push({
          field: 'priority',
          values: filters.priority,
        });
      }

      if (filters.workOrderName && filters.workOrderName.length > 0) {
        serverFilters.push({
          field: 'work_order_name',
          values: filters.workOrderName,
        });
      }

      filterBody = serverFilters.length > 0 ? { filters: serverFilters } : undefined;
    }

    const body: FindWorkTasksBody | undefined = filterBody;
    const response = await findWorkTasks(body, queryParams, signal);
    return response || [];
  },

  /**
   * Extract TP2 work task IDs from work task results.
   * Used to aggregate work task IDs for fetching associated assets.
   *
   * @param workTasks - Array of work task DTOs
   * @returns Array of TP2 work task IDs (tp2WorkRecordId values)
   */
  extractTp2WorkTaskIds(workTasks: WorkTaskDto[]): number[] {
    if (!workTasks || workTasks.length === 0) {
      return [];
    }

    return workTasks
      .map((task) => task.tp2WorkRecordId)
      .filter((id): id is number => id !== null && id !== undefined);
  },
};

export { WorkTaskRepository as default };
